import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useReducedMotion } from "framer-motion";
import { LandingNavbar } from "../components/landing/LandingNavbar";
import { Hero } from "../components/landing/Hero";
import { Capabilities } from "../components/landing/Capabilities";
import { ProductTour } from "../components/landing/ProductTour";
import { Verticals } from "../components/landing/Verticals";
import { EarlyAccess } from "../components/landing/EarlyAccess";
import { FAQ } from "../components/landing/FAQ";
import { FinalCTA } from "../components/landing/FinalCTA";
import { LandingFooter } from "../components/landing/LandingFooter";
import { ConsentBanner } from "../components/landing/ConsentBanner";
import { CookiePreferencesModal } from "../components/landing/CookiePreferencesModal";
import { COOKIE_PREFS_EVENT } from "../components/landing/cookiePreferences";
import { usePageMeta } from "../hooks/usePageMeta";
import {
  useLandingInstrumentation,
  useLandingPageView,
} from "../hooks/useLandingAnalytics";

export function LandingPage() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language ?? "en").split("-")[0];
  const location = useLocation();
  const prefersReduced = useReducedMotion();
  const [prefsOpen, setPrefsOpen] = useState(false);

  // Open the cookie-preferences dialog from the soft notice's "Manage" button
  // and the footer "Cookie preferences" link (both dispatch COOKIE_PREFS_EVENT).
  useEffect(() => {
    const open = () => setPrefsOpen(true);
    window.addEventListener(COOKIE_PREFS_EVENT, open);
    return () => window.removeEventListener(COOKIE_PREFS_EVENT, open);
  }, []);

  usePageMeta({
    title: t("landing.meta.title"),
    description: t("landing.meta.description"),
    ogLocale: lang === "es" ? "es_MX" : "en_US",
    lang,
  });
  useLandingPageView();
  useLandingInstrumentation();

  // A refresh must load the landing at the top: in-page nav never writes a
  // hash (see scrollToSection.ts), and while this page is mounted we take
  // over the browser's scroll restoration, which would otherwise re-land the
  // visitor mid-page on reload.
  useEffect(() => {
    const prev = "scrollRestoration" in window.history ? window.history.scrollRestoration : null;
    if (prev !== null) window.history.scrollRestoration = "manual";
    if (!window.location.hash) {
      try {
        window.scrollTo(0, 0);
      } catch {
        // jsdom — scrollTo is not implemented; irrelevant outside a browser.
      }
    }
    return () => {
      if (prev !== null) window.history.scrollRestoration = prev;
    };
  }, []);

  // Scroll to section anchors — covers external arrivals like /#pricing and
  // the hash-preserving /landing redirect.
  useEffect(() => {
    if (!location.hash) return;
    const el = document.querySelector(location.hash);
    el?.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth" });
  }, [location.hash, prefersReduced]);

  return (
    <div className="min-h-screen overflow-x-hidden bg-white font-sans text-slate-900 dark:bg-slate-950 dark:text-white">
      <LandingNavbar />
      <main>
        <Hero />
        <Capabilities />
        <ProductTour />
        <Verticals />
        <EarlyAccess />
        <FAQ />
        <FinalCTA />
      </main>
      <LandingFooter />
      <ConsentBanner />
      <CookiePreferencesModal
        open={prefsOpen}
        onClose={() => setPrefsOpen(false)}
      />
    </div>
  );
}
